import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  Lock,
  Upgrade,
  Star,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { fetchSubscription, startTrial, checkUsageLimit } from '../../store/slices/subscriptionSlice';

interface FeatureGateProps {
  children: React.ReactNode;
  feature?: 'csvImport' | 'csvExport' | 'advancedAnalytics' | 'teamCollaboration' | 'apiAccess' | 'customBranding';
  action?: 'recipe' | 'waterProfile' | 'formulation' | 'customSalt';
  requiredTier?: 'PROFESSIONAL' | 'ENTERPRISE';
  fallback?: React.ReactNode;
  showUpgradePrompt?: boolean;
}

const FeatureGate: React.FC<FeatureGateProps> = ({
  children,
  feature,
  action,
  requiredTier = 'PROFESSIONAL',
  fallback,
  showUpgradePrompt = true,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { subscription, usage, limits, isLoading } = useSelector((state: RootState) => state.subscription);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [limitCheckResult, setLimitCheckResult] = useState<any>(null);

  useEffect(() => {
    if (isAuthenticated && !subscription) {
      dispatch(fetchSubscription());
    }
  }, [dispatch, isAuthenticated, subscription]);

  useEffect(() => {
    if (action && subscription) {
      dispatch(checkUsageLimit(action)).then((result) => {
        if (result.payload) {
          setLimitCheckResult(result.payload);
        }
      });
    }
  }, [dispatch, action, subscription]);

  // Check if user has access to the feature
  const hasFeatureAccess = () => {
    if (!subscription) return false;
    
    // Check tier access
    if (requiredTier === 'ENTERPRISE' && subscription.tier !== 'ENTERPRISE') {
      return false;
    }
    if (requiredTier === 'PROFESSIONAL' && !['PROFESSIONAL', 'ENTERPRISE'].includes(subscription.tier)) {
      return false;
    }

    return true;
  };

  // Check if user has reached usage limits
  const hasUsageLimit = () => {
    if (!action || !usage || !limits) return false;

    switch (action) {
      case 'recipe':
        return limits.recipesPerMonth !== -1 && usage.recipesCreated >= limits.recipesPerMonth;
      case 'waterProfile':
        return limits.waterProfilesTotal !== -1 && usage.waterProfilesCreated >= limits.waterProfilesTotal;
      case 'formulation':
        return limits.formulationsTotal !== -1 && usage.formulationsCreated >= limits.formulationsTotal;
      case 'customSalt':
        return limits.customSaltsTotal !== -1 && usage.customSaltsCreated >= limits.customSaltsTotal;
      default:
        return false;
    }
  };

  const getUsagePercentage = () => {
    if (!action || !usage || !limits) return 0;

    switch (action) {
      case 'recipe':
        return limits.recipesPerMonth === -1 ? 0 : (usage.recipesCreated / limits.recipesPerMonth) * 100;
      case 'waterProfile':
        return limits.waterProfilesTotal === -1 ? 0 : (usage.waterProfilesCreated / limits.waterProfilesTotal) * 100;
      case 'formulation':
        return limits.formulationsTotal === -1 ? 0 : (usage.formulationsCreated / limits.formulationsTotal) * 100;
      case 'customSalt':
        return limits.customSaltsTotal === -1 ? 0 : (usage.customSaltsCreated / limits.customSaltsTotal) * 100;
      default:
        return 0;
    }
  };

  const getCurrentUsage = () => {
    if (!action || !usage) return 0;

    switch (action) {
      case 'recipe':
        return usage.recipesCreated;
      case 'waterProfile':
        return usage.waterProfilesCreated;
      case 'formulation':
        return usage.formulationsCreated;
      case 'customSalt':
        return usage.customSaltsCreated;
      default:
        return 0;
    }
  };

  const getLimit = () => {
    if (!action || !limits) return 0;

    switch (action) {
      case 'recipe':
        return limits.recipesPerMonth;
      case 'waterProfile':
        return limits.waterProfilesTotal;
      case 'formulation':
        return limits.formulationsTotal;
      case 'customSalt':
        return limits.customSaltsTotal;
      default:
        return 0;
    }
  };

  const handleStartTrial = async () => {
    try {
      await dispatch(startTrial()).unwrap();
      setUpgradeDialogOpen(false);
    } catch (error) {
      console.error('Failed to start trial:', error);
    }
  };

  const isTrialEligible = subscription?.tier === 'FREE' && !subscription?.trialEnd;

  // If not authenticated, don't show anything
  if (!isAuthenticated) {
    return null;
  }

  // If loading, show loading state
  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Checking access permissions...
        </Typography>
      </Box>
    );
  }

  // Check feature access
  if (feature && !hasFeatureAccess()) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <Card sx={{ border: '2px dashed', borderColor: 'warning.main', bgcolor: 'warning.light', opacity: 0.7 }}>
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          <Lock sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {requiredTier} Feature
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            This feature requires a {requiredTier.toLowerCase()} subscription.
          </Typography>
          {showUpgradePrompt && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Upgrade />}
              onClick={() => setUpgradeDialogOpen(true)}
            >
              Upgrade Now
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // Check usage limits
  if (action && hasUsageLimit()) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <Card sx={{ border: '2px dashed', borderColor: 'error.main', bgcolor: 'error.light', opacity: 0.7 }}>
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          <Warning sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Usage Limit Reached
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            You've reached your {action} limit ({getCurrentUsage()}/{getLimit()}).
          </Typography>
          {showUpgradePrompt && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Upgrade />}
              onClick={() => setUpgradeDialogOpen(true)}
            >
              Upgrade for More
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // Show usage warning if approaching limit
  const usagePercentage = getUsagePercentage();
  const showUsageWarning = action && usagePercentage >= 80 && usagePercentage < 100;

  return (
    <>
      {showUsageWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            You're approaching your {action} limit ({getCurrentUsage()}/{getLimit()}).
            {showUpgradePrompt && (
              <Button
                size="small"
                color="inherit"
                onClick={() => setUpgradeDialogOpen(true)}
                sx={{ ml: 1 }}
              >
                Upgrade
              </Button>
            )}
          </Typography>
        </Alert>
      )}
      
      {children}

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialogOpen} onClose={() => setUpgradeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star color="primary" />
            Upgrade Your Plan
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Unlock more features and higher limits with a Professional or Enterprise plan.
          </Typography>
          
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Professional Plan
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • 100 recipes per month
                  • 5 custom water profiles
                  • 20 custom formulations
                  • Advanced analytics
                  • CSV import/export
                </Typography>
                {isTrialEligible && (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleStartTrial}
                  >
                    Start 14-Day Free Trial
                  </Button>
                )}
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpgradeDialogOpen(false)}>
            Maybe Later
          </Button>
          <Button variant="contained" color="primary">
            View All Plans
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeatureGate;
